import React, { FC, ReactElement, useEffect, useState } from 'react';
import { CookiesPreferences } from '../CookiesPreferences';
import { Trans } from '../../shared/Trans';
import { Link } from '../../shared/Link';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { CookieIcon } from '../../shared/SVG/Icons/CookieIcon';
import { NotificationBar } from '../../shared/NotificationBar';
import { MediaBreakpoint, useBreakpointInfo } from '../../../hooks/useDeviceInfo';
import { useCookies } from '../../../hooks/cookies';
import { APP_URLS } from '../../../consts';
import { isCookieFalsy } from '../../../utils/cookies';
import { useLocation } from 'react-router-dom';
import { useUserContext } from '../../../providers/UserProvider';
import './CookieBanner.scss';

const CookieBannerContent = ({ onUpdate, theme }: { onUpdate: () => void; theme: string }): ReactElement => {
  return (
    <p className="cookie-content">
      <Trans keyProp="cookie-banner-full-text">
        Cookies are used to personalise content, track website usage and make our games function. You can see our full{' '}
        <Link href={APP_URLS.itv.cookiePolicy.replace(':brand', theme)}>Cookie Policy</Link> or change your{' '}
        <Link onClick={onUpdate}>Cookie Preferences</Link>
      </Trans>
    </p>
  );
};

export const CookieBanner: FC<{
  showCookiePreferencesOuter?: boolean;
  setShowCookiePreferencesOuter?: (arg: boolean) => void;
}> = ({ showCookiePreferencesOuter = false, setShowCookiePreferencesOuter }) => {
  const { acceptAllConsent, rejectAllConsent } = useCookies();
  const location = useLocation();
  const { theme } = useUserContext();

  const isBelowMobileWidth = useBreakpointInfo([MediaBreakpoint.BELOW_MOBILE]);

  const [showCookiesPreferences, setShowCookiesPreferences] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  const { t } = useTranslations();

  const onManageCookiesClick = (): void => {
    setShowCookiesPreferences(true);
  };

  const onClickAcceptAll = (): void => {
    acceptAllConsent();
    setShowCookieBanner(false);
  };

  const onClickRejectAll = (): void => {
    rejectAllConsent();
    setShowCookieBanner(false);
  };

  useEffect(() => {
    isCookieFalsy() && setShowCookieBanner(true);
  }, []);

  useEffect(() => setShowCookiesPreferences(showCookiePreferencesOuter), [showCookiePreferencesOuter]);

  useEffect(() => setShowCookiePreferencesOuter?.(showCookiesPreferences), [showCookiesPreferences]);

  return (
    <>
      {showCookieBanner &&
        location.pathname !== APP_URLS.minigames &&
        new URLSearchParams(location.search).get('minigame') !== 'true' && (
          <NotificationBar
            className="cookie-banner"
            isShow
            disableSiteContent
            imgNode={<CookieIcon />}
            message={CookieBannerContent({ onUpdate: onManageCookiesClick, theme })}
            buttonText={t('cookie-banner-button-text')}
            buttonSize={isBelowMobileWidth ? 'medium' : 'large'}
            onClick={onClickAcceptAll}
            TertiaryButton={{
              type: 'button',
              buttonVariant: 'primary',
              onClick: onClickRejectAll,
              size: isBelowMobileWidth ? 'medium' : 'large',
              text: t('cookie-banner-reject-btn-text'),
            }}
            SecondaryButton={{
              type: 'button',
              buttonVariant: 'text',
              onClick: onManageCookiesClick,
              size: isBelowMobileWidth ? 'medium' : 'large',
              text: t('cookie-banner-secondary-btn-text'),
            }}
          />
        )}
      {showCookiesPreferences && (
        <CookiesPreferences
          onClose={(): void => {
            setShowCookiesPreferences(false);
          }}
          setShowCookieBanner={setShowCookieBanner}
        />
      )}
    </>
  );
};
